// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * D-SUPPORT-3: Support settings — the global AI assistant configuration
 * (support_manager via support.settings.manage, or platform admin).
 *  - Anthropic API key (stored server-side; only the last 4 shown back)
 *  - Model id · max tokens per reply · DAILY token budget with live usage
 * PROD NOTE (in the design doc): the key belongs in Key Vault before production;
 * ops_settings storage is the lab rail. Sasha's BAA gate applies to a real key.
 */
import { useEffect, useState } from 'react'
import { Settings2, KeyRound, Gauge } from 'lucide-react'
import * as api from '../../api/client'
import BackToSupportTools from './BackToSupportTools'
import { friendlyError } from '../../api/errors'
import { ErrorBanner, LoadingState } from '../../components/workflow/StatePills'
import SearchableSelect from '../../components/common/SearchableSelect'

// Models the assistant can run on. Ordered most-capable first, since that is how the choice is
// usually reasoned about — cost is the trade being made against it, and the note under the picker
// says so for each.
const MODELS: { value: string; label: string; note: string }[] = [
  { value: 'claude-opus-5',            label: 'Claude Opus 5 — most capable',   note: 'Best reasoning over messy tickets and logs. Highest cost per reply — watch the daily budget.' },
  { value: 'claude-sonnet-5',          label: 'Claude Sonnet 5 — balanced',     note: 'The default. Strong enough for triage and lookups at a fraction of Opus cost.' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 — fastest',    note: 'Cheapest and quickest. Good for short lookups; weaker at multi-step reasoning across tools.' },
  { value: 'claude-fable-5',           label: 'Claude Fable 5',                 note: 'Alternative Claude 5 model.' },
]

const MODEL_NOTES: Record<string, string> = Object.fromEntries(MODELS.map(m => [m.value, m.note]))

// Always include whatever is CURRENTLY stored, even if it is not in the list above. Opening this
// page must never quietly rewrite a model id that someone set deliberately or that shipped after
// this list was written.
const MODEL_OPTIONS = (current: string) =>
  MODELS.some(m => m.value === current) || !current
    ? MODELS.map(m => ({ value: m.value, label: m.label }))
    : [...MODELS.map(m => ({ value: m.value, label: m.label })), { value: current, label: `${current} (currently configured)` }]

export default function SupportSettingsPage() {
  const [data, setData] = useState<api.SupportAiSettings | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [ok, setOk] = useState(false)
  const [busy, setBusy] = useState(false)
  const [keyInput, setKeyInput] = useState('')
  const [model, setModel] = useState('')
  const [maxTokens, setMaxTokens] = useState('')
  const [dailyCap, setDailyCap] = useState('')
  const [notifyEmails, setNotifyEmails] = useState('')
  const [subjectMode, setSubjectMode] = useState('include')
  // D-SUPPORT-16: the read-tools switch. The API has accepted tools_enabled since D-SUPPORT-5 and
  // the client binding carried it, but NO CONTROL EVER RENDERED IT — so the only way to turn the
  // assistant's data access on was a direct ops_settings write. Same "built the endpoint, never
  // surfaced it" gap that hid customer health behind a working API earlier this week.
  const [toolsEnabled, setToolsEnabled] = useState(false)

  const load = () => api.getSupportAiSettings().then(d => {
    setData(d); setModel(d.model); setMaxTokens(String(d.max_tokens)); setDailyCap(String(d.daily_token_cap)); setNotifyEmails(d.notify_emails ?? ''); setSubjectMode(d.subject_mode || 'include'); setToolsEnabled(d.tools_enabled === true)
  }).catch(e => setError(friendlyError(e)))
  useEffect(() => { void load() }, [])

  const save = async (clearKey = false) => {
    setBusy(true); setError(null); setOk(false)
    try {
      await api.putSupportAiSettings({
        ...(clearKey ? { api_key: '' } : keyInput.trim() ? { api_key: keyInput.trim() } : {}),
        model: model.trim() || undefined,
        max_tokens: Number(maxTokens) || undefined,
        daily_token_cap: dailyCap === '' ? undefined : Math.max(0, Number(dailyCap)),
        notify_emails: notifyEmails,
        subject_mode: subjectMode,
        tools_enabled: toolsEnabled,
      })
      setKeyInput(''); setOk(true)
      await load()
    } catch (e) { setError(friendlyError(e)) }
    finally { setBusy(false) }
  }

  const pct = data && data.daily_token_cap > 0 ? Math.min(100, Math.round(data.used_today / data.daily_token_cap * 100)) : null

  return (
    <div style={{ padding: '24px 24px 48px', maxWidth: '100%', margin: '0 auto', width: '100%' }}>
      <header style={{ marginBottom: 16 }}>
        <BackToSupportTools />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 8 }}><Settings2 size={21} /> Support settings</h1>
        <div style={{ color: 'var(--color-text-mute)', fontSize: 13, marginTop: 4 }}>
          Global configuration for the support AI assistant. Changes apply to the whole support team.
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}
      {ok && <div style={{ background: '#dcfce7', color: '#15803d', borderRadius: 8, padding: '8px 12px', fontSize: 13, marginBottom: 12 }}>Saved.</div>}
      {!data ? <LoadingState label="Loading settings…" /> : (
        <>
          <section style={card}>
            <h2 style={h2}><KeyRound size={15} /> API key</h2>
            <div style={{ fontSize: 13, color: 'var(--color-text-mute)', marginBottom: 10 }}>
              {data.configured
                ? <>Configured — key ending in <b>…{data.key_last4}</b>. The assistant answers with the real model.</>
                : <>Not configured — the assistant runs the built-in demo knowledge base. Paste an Anthropic API key to enable the full assistant.</>}
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <input type="password" value={keyInput} onChange={e => setKeyInput(e.target.value)}
                     placeholder={data.configured ? 'Paste a new key to replace' : 'sk-ant-…'}
                     autoComplete="new-password"
                     style={{ ...input, flex: 1, minWidth: 260 }} />
              {data.configured && (
                <button onClick={() => void save(true)} disabled={busy} style={{ ...btn, background: '#fee2e2', color: '#b91c1c' }}>
                  Remove key
                </button>
              )}
            </div>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 8 }}>
              Stored server-side; never shown back in full. Production keys go to Key Vault (Mario) and need the
              compliance BAA attestation (Sasha) — see D-SUPPORT-3 §4.
            </div>
          </section>

          <section style={card}>
            <h2 style={h2}><Gauge size={15} /> Model & limits</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <label style={lab}>Model
                {/* A picker, not free text. A typo here does not fail validation — it fails at the
                    first chat with an upstream error, which reads as "the assistant is broken".
                    Whatever is currently stored is always offered, so an unrecognised or newer
                    model id is never silently replaced by opening this page. */}
                <SearchableSelect value={model} onChange={setModel} ariaLabel="Assistant model"
                  options={MODEL_OPTIONS(model)} style={input} />
                <span style={{ fontSize: 11.5, color: 'var(--color-text-mute)', fontWeight: 400, marginTop: 4 }}>
                  {MODEL_NOTES[model] ?? 'Custom model id — kept as configured.'}
                </span>
              </label>
              <label style={lab}>Max tokens per reply (1–8192)
                <input type="number" value={maxTokens} onChange={e => setMaxTokens(e.target.value)} style={input} min={1} max={8192} />
              </label>
              <label style={{ ...lab, gridColumn: 'span 2' }}>Daily token budget (0 = unlimited)
                <input type="number" value={dailyCap} onChange={e => setDailyCap(e.target.value)} style={input} min={0} step={1000} />
              </label>
            </div>
            <div style={{ marginTop: 12, fontSize: 13 }}>
              <b>Used today:</b> {data.used_today.toLocaleString()} tokens · {data.requests_today} requests
              {pct !== null && <> · <b>{pct}%</b> of budget</>}
              {pct !== null && (
                <div style={{ marginTop: 6, height: 8, borderRadius: 999, background: 'var(--color-bg)', overflow: 'hidden', border: '1px solid var(--color-border)' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: pct >= 90 ? '#b91c1c' : pct >= 70 ? '#b45309' : 'var(--color-primary, #0f766e)' }} />
                </div>
              )}
              <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 6 }}>
                When the budget is spent, the assistant refuses further AI calls until midnight UTC (agents get an honest message; the demo knowledge base is unaffected).
              </div>
            </div>
          </section>


          <section style={card}>
            <h2 style={h2}>Notifications</h2>
            <label style={{ ...lab, marginBottom: 10 }}>Support team recipients (comma-separated emails)
              <input value={notifyEmails} onChange={e => setNotifyEmails(e.target.value)} style={input}
                     placeholder="support@owlconsultations.com, oncall@…" />
            </label>
            <label style={lab}>Ticket subject in team emails
              <SearchableSelect value={subjectMode} onChange={setSubjectMode} ariaLabel="Ticket subject mode"
                options={[
                  { value: 'include', label: 'Include subject (D3 ruling — subjects may contain PHI, Sasha flag open)' },
                  { value: 'redact', label: 'Redact subject (link + case number only)' },
                ]} style={input} />
            </label>
            <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', marginTop: 8 }}>
              These control the OWL TEAM emails (new ticket / activity / SLA breach rails). Customer-facing
              emails are always link-only and are not configurable here by design.
            </div>
          </section>

          <section style={card}>
            <h2 style={h2}>Assistant data access</h2>
            <label style={{ display: 'flex', gap: 9, alignItems: 'flex-start', cursor: 'pointer' }}>
              <input type="checkbox" checked={toolsEnabled} onChange={e => setToolsEnabled(e.target.checked)}
                     style={{ marginTop: 3 }} />
              <span>
                <span style={{ fontWeight: 600, fontSize: 13.5 }}>Let the assistant read live data</span>
                <span style={{ display: 'block', fontSize: 12, color: 'var(--color-text-mute)', marginTop: 3, lineHeight: 1.55 }}>
                  With this OFF the assistant answers from a static knowledge base and says so when
                  asked something it cannot look up. With it ON it can read tickets, queue stats,
                  platform health, tenant resource metrics, the <b>customer health / CSM risk matrix</b>,
                  and — for anyone holding <code>support.logs.read</code> — the system log.
                  All tools are read-only: parameterised SELECTs, server-side row caps, no write verbs.
                </span>
                <span style={{ display: 'block', fontSize: 12, color: '#b45309', marginTop: 6, lineHeight: 1.55 }}>
                  This changes what data leaves for the model. <b>D-SUPPORT-5 §4 is still open with
                  Sasha</b> on whether customer-authored ticket <i>subjects</i> may be sent — the
                  subject-mode control above is the related lever. Turning this on is audited.
                </span>
              </span>
            </label>
          </section>

          <button onClick={() => void save(false)} disabled={busy} style={{ ...btn, background: 'var(--color-primary, #0f766e)', color: '#fff' }}>
            {busy ? 'Saving…' : 'Save settings'}
          </button>
        </>
      )}
    </div>
  )
}

const card: React.CSSProperties = { background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 14 }
const h2: React.CSSProperties = { fontSize: 15, fontWeight: 700, margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: 7 }
const lab: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 5, fontSize: 12.5, fontWeight: 600, color: 'var(--color-text-mute)' }
const input: React.CSSProperties = { padding: '9px 11px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 13.5, background: 'var(--color-bg)', color: 'var(--color-text)', fontWeight: 400 }
const btn: React.CSSProperties = { border: 'none', borderRadius: 9, padding: '10px 16px', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }
